import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Separator } from 'designSystem/components/Separator'
import { InstrumentTimeSeriesPreview } from 'features/knowledgeDocumentViewer/components/viewer/shared/InstrumentTimeSeriesPreview'
import { GraphSummaryDetails } from 'features/knowledgeDocumentViewer/graph/GraphSummaryDetails'
import type { NodeOverlayInspection } from 'features/knowledgeDocumentViewer/graph/useNodeOverlayInspection'
import type { MatchedDatapoint } from 'features/timeSeries'
import { type ReactElement, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { buildNodeTooltip } from './nodeTooltip'
import {
  isPipeOffPageConnector,
  resolvePeerOffPageConnector,
} from './offPageConnectorGraph'

const Card = styled.div.attrs<{
  $left: number
  $top: number
  $width: number
  $height: number
}>((props) => ({
  style: {
    left: props.$left,
    top: props.$top,
    maxWidth: props.$width,
    maxHeight: props.$height,
  },
}))<{ $left: number; $top: number; $width: number; $height: number }>`
  position: fixed;
  z-index: 1001;
  pointer-events: auto;
  width: 25rem;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.625rem 0.875rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.375rem;
  background: var(--popover);
  color: var(--popover-foreground);
  box-shadow: var(--shadow-sm);
  font-size: 0.8125rem;
  overflow-wrap: break-word;
  p {
    margin: 0.125rem 0;
    color: var(--muted-foreground);
  }
`

const SectionDivider = styled.div`
  margin: 0.625rem 0;
`

export function GraphTooltip({
  inspection,
  onDismiss,
  datapoints = [],
}: {
  inspection: NodeOverlayInspection
  onDismiss: () => void
  datapoints?: MatchedDatapoint[]
}): ReactElement {
  useLingui()
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 352, height: 180 })
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver((): void => {
      const rect = element.getBoundingClientRect()
      setSize((previous) =>
        previous.width === rect.width && previous.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height }
      )
    })
    observer.observe(element)
    return (): void => observer.disconnect()
  }, [])
  const { bounds, anchor, overlay } = inspection
  const tooltip = buildNodeTooltip(overlay.node)
  const peer = resolvePeerOffPageConnector(overlay.node)
  const left = Math.max(
    bounds.left + 8,
    Math.min(anchor.x + 14, bounds.left + bounds.width - size.width - 8)
  )
  const top = Math.max(
    bounds.top + 8,
    Math.min(anchor.y + 14, bounds.top + bounds.height - size.height - 8)
  )
  return (
    <Card
      ref={ref}
      role='tooltip'
      tabIndex={0}
      data-graph-tooltip
      onBlur={(event): void => {
        if (
          event.relatedTarget instanceof Node &&
          event.currentTarget.contains(event.relatedTarget)
        )
          return
        onDismiss()
      }}
      onPointerLeave={(): void => {
        if (!ref.current?.contains(document.activeElement)) onDismiss()
      }}
      $left={left}
      $top={top}
      $width={Math.max(0, bounds.width - 16)}
      $height={Math.max(0, bounds.height - 16)}>
      <strong>{tooltip.title}</strong>
      {tooltip.subtitle && <p>{tooltip.subtitle}</p>}
      <GraphSummaryDetails rows={tooltip.rows} />
      {tooltip.rows.length > 0 && datapoints.length > 0 && (
        <SectionDivider>
          <Separator />
        </SectionDivider>
      )}
      {datapoints.length > 0 && (
        <InstrumentTimeSeriesPreview
          key={JSON.stringify([
            overlay.node.id,
            datapoints.map(({ id }) => id),
          ])}
          matchedDatapoints={datapoints}
          clickHint={null}
        />
      )}
      {peer && (
        <p>
          <Trans>Click to open the connected page.</Trans>
        </p>
      )}
      {isPipeOffPageConnector(overlay.node) && !peer && (
        <p>
          <Trans>The connected page has not been resolved in the graph.</Trans>
        </p>
      )}
    </Card>
  )
}
